import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useUserContext } from "../../context/UserProvider";
import { LocationIcon, MailIcon, SprintIcon } from "../util/Icons";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db, storage } from "../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import { GarminToggle } from "../util/Components";
import { Link } from "react-router-dom";
import ProfilePicture, { useProfilePicturesProvider } from "../util/ProfilePicture";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import confirmPopup from "../util/ConfirmPopup";
import ConnectProviderCards, { FITBIT_ANDROID_INSTRUCTIONS_URL, FITBIT_IPHONE_INSTRUCTIONS_URL } from "./ConnectProviderCards";
import { FITBIT_CONNECTION_CUTOFF_MS, hasFitbitConnectionEnded } from "../../utils/activityProviders";

const FitbitInstructionLinks = ({ className = "", style }) => (
  <div className={`flex flex-wrap gap-x-4 gap-y-1 ${className}`}>
    <a className="font-semibold underline" style={style} href={FITBIT_IPHONE_INSTRUCTIONS_URL} target="_blank" rel="noopener noreferrer">
      iPhone instructions
    </a>
    <a className="font-semibold underline" style={style} href={FITBIT_ANDROID_INSTRUCTIONS_URL} target="_blank" rel="noopener noreferrer">
      Android instructions
    </a>
  </div>
);

const AccountPage = () => {
  const { user, userDetails, userData, userRaces, linkedDevice } = useUserContext();
  const [showStravaConnect, setShowStravaConnect] = useState(false);
  const [showStravaInfo, setShowStravaInfo] = useState(false);
  const [garminConnectEnabled, setGarminConnectEnabled] = useState(true);
  const [racesMap, setRacesMap] = useState(undefined);
  const { setPicturesMap, getURL } = useProfilePicturesProvider();
  const [profilePicURL, setProfilePicURL] = useState(null);
  const [activityTimeZone, setActivityTimeZone] = useState("");
  const [savingActivityTimeZone, setSavingActivityTimeZone] = useState(false);
  const [activityTimeZoneMessage, setActivityTimeZoneMessage] = useState("");
  const [fitbitConnectionEnded, setFitbitConnectionEnded] = useState(hasFitbitConnectionEnded);
  useEffect(() => {
    if (linkedDevice !== "Fitbit") return undefined;

    let timer;
    function updateDeadlineNotice() {
      clearTimeout(timer);
      const now = Date.now();
      setFitbitConnectionEnded(hasFitbitConnectionEnded(now));
      if (now < FITBIT_CONNECTION_CUTOFF_MS) {
        timer = setTimeout(updateDeadlineNotice, Math.min(FITBIT_CONNECTION_CUTOFF_MS - now, 2147483647));
      }
    }
    updateDeadlineNotice();
    window.addEventListener("focus", updateDeadlineNotice);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("focus", updateDeadlineNotice);
    };
  }, [linkedDevice]);

  useEffect(() => {
    setActivityTimeZone(userData?.timeZone || "");
  }, [userData?.timeZone]);

  async function saveActivityTimeZone() {
    const timeZone = activityTimeZone.trim();
    try {
      new Intl.DateTimeFormat("en-US", {timeZone}).format(new Date());
    } catch (_error) {
      setActivityTimeZoneMessage("Enter a valid timezone, such as America/Toronto.");
      return;
    }
    setSavingActivityTimeZone(true);
    setActivityTimeZoneMessage("");
    try {
      await updateDoc(doc(db, "users", user.uid), {timeZone});
      setActivityTimeZoneMessage("Activity timezone saved.");
    } catch (_error) {
      setActivityTimeZoneMessage("Could not save your activity timezone. Try again.");
    } finally {
      setSavingActivityTimeZone(false);
    }
  }

  const activityTimeZoneOptions = typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : ["America/Toronto", "America/Vancouver", "America/St_Johns", "UTC"];
  useEffect(() => {
    async function getPicURL() {
      // let url = await getDownloadURL(ref(storage, `/profile_pictures/${user.uid}`)).catch((err) => {});
      let url = await getURL(user.uid);
      setProfilePicURL(url);
    }
    if (!profilePicURL) getPicURL();
  }, [user?.uid]);

  const isStaging = window.location.origin === "https://mcc-staging-4525c.web.app" || window.location.origin === "http://localhost:3000";

  async function getAllRaces() {
    if (userRaces) {
      let tempRacesMap = [];
      let sortedRaces = Array.from(userRaces).sort((a, b) => {
        let date1 = new Date(a[1].raceInfo.raceDateStart);
        let date2 = new Date(b[1].raceInfo.raceDateStart);

        return date2.getTime() - date1.getTime();
      });

      for (const value of sortedRaces) {
        let giftCodesMap = [];
        // console.log(key, value);
        const e = value[1];
        if (e.giftCodes) {
          for (let id in e.giftCodes) {
            // console.log(id, e.giftCodes[id])
            const giftCodeDocRef = await getDoc(doc(db, "giftcodes", id));

            giftCodesMap.push({ id: id, name: e.giftCodes[id], used: giftCodeDocRef.data().used });
          }
        }
        // console.log(e);
        tempRacesMap.push(
          <div
            key={`${e.raceInfo.name}-${e.raceInfo.legName}`}
            className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm"
          >
            <Link
              className="grid w-full grid-cols-[56px,1fr] items-center gap-3 px-3 py-3 text-slate-900 no-underline transition-colors hover:bg-slate-50 hover:no-underline"
              to={`/dashboard/${e.legId.replace(/^price_/, "")}`}
            >
              <img
                className="h-14 w-14 rounded-md object-contain"
                src={e.raceInfo?.imageUrl}
                alt={`${e.raceInfo?.name}-logo`}
              />
              <div className="min-w-0">
                <p className="font-semibold text-base mb-0">{e.raceInfo?.name}</p>
                <p className="mb-1 text-xs text-slate-600">{e.raceInfo?.legName}</p>
                <p className="mb-0 text-xs font-medium text-slate-500">{e.raceInfo?.date}</p>
              </div>
            </Link>
            {giftCodesMap.length > 0 && (
              <div className="border-t border-slate-200 bg-slate-50 px-3 py-3">
                <p className="font-semibold text-sm mt-0 mb-2">Gift Registration Coupons Purchased</p>
                {giftCodesMap.map((code) => {
                  return (
                    <div key={code.id} className="flex items-center gap-3 text-sm text-slate-700">
                      <div className="w-4 text-center">{code.used ? "✓" : "o"}</div>
                      <p key={code.id} className="m-0">
                        {code.id} ({code.name})
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      }
      // })
      // console.log(tempRacesMap);
      setRacesMap((prev) => tempRacesMap);
    }
  }
  useEffect(() => {
    getAllRaces();
  }, [userRaces]);

  async function uploadProfilePicture(e) {
    const picture = e?.target?.files[0] || null;
    if (!picture) return;
    console.log("Uploading ", calcFileSize(picture.size));
    let picPath = `profile_pictures/${user.uid}`;
    let picRef = ref(storage, picPath);
    await uploadBytes(picRef, picture);
    console.log("Upload complete");
    const downLoadURL = await getDownloadURL(picRef);
    setPicturesMap(user.uid, downLoadURL);
    setProfilePicURL(downLoadURL);
  }

  async function deleteProfilePicture() {
    let confirm = await confirmPopup({ textY: "Yes", textN: "No" });
    if (!confirm) return;
    console.log("Deleting pic");
    await deleteObject(ref(storage, `/profile_pictures/${user.uid}`))
      .then(() => {
        console.log("Delete success");
        setPicturesMap(user.uid, null);
        setProfilePicURL(null);
      })
      .catch((err) => {
        console.error(err.message);
      });
  }
  function requestStravaAuth() {
    const appId = isStaging ? "107147" : "104555";
    window.open(
      `http://www.strava.com/oauth/authorize?client_id=${appId}&response_type=code&redirect_uri=${window.location.origin}/sconfirm&approval_prompt=force&scope=activity:read&state=`,
      "_self"
    );
  }

  async function requestGarminAuth() {
    // const tempCallbackURL = encodeURIComponent("http://localhost:3000/garminAuth");
    const getOAuthRequestToken = httpsCallable(getFunctions(), "garmin-getOAuthRequestToken");
    try {
      const result = await getOAuthRequestToken();
      const token = result.data.temporaryToken;
      if (!token) {
        throw new Error("No token");
      }
      // Temp testing callback
      const garminConfirmUrl = `https://connect.garmin.com/oauthConfirm?oauth_token=${token}`;
      // const garminConfirmUrl = `https://connect.garmin.com/oauthConfirm?oauth_token=${token}&oauth_callback=${tempCallbackURL}`;
      setGarminConnectEnabled(true);
      window.open(garminConfirmUrl, "_self");
    } catch (err) {
      console.error(err);
      setGarminConnectEnabled(true);
    }
  }
  return (
    <div className="relative mx-auto max-w-3xl w-full mt-6">
      {showStravaInfo && <StravaFreeInfoOverlay onClose={() => setShowStravaInfo(false)} />}
      <h2 className="mb-4">Personal Information</h2>
      <div className="flex gap-2 mb-3 items-center">
        <ProfilePicture userID={user?.uid && user.uid} className={" !w-32 !h-32 text-3xl"} refreshToken={profilePicURL} />
        <label htmlFor="profilePictureInput" className="border rounded-lg h-fit px-2 cursor-pointer select-none">
          <span>Change Profile Picture</span>
          <input type="file" accept="image/*" id="profilePictureInput" style={{ display: "none" }} onInput={uploadProfilePicture} />
        </label>
        {profilePicURL && (
          <button className="border rounded-lg h-fit px-2 cursor-pointer select-none" onClick={deleteProfilePicture}>
            Delete Picture
          </button>
        )}
      </div>
      <div className="grid grid-cols-[42px,1fr] mb-4 items-center">
        <MailIcon />
        <p className="font-semibold mb-0">{user.email}</p>
      </div>
      {linkedDevice && linkedDevice !== "none" && (
        <div className="grid grid-cols-[42px,1fr] items-center mb-3">
          <LocationIcon />
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-semibold mb-0">
              {linkedDevice === "Google Health Connect"
                ? "You are connected to Google Health Connect through the Android app."
                : `You are connected with ${linkedDevice}!`}
            </p>
            {linkedDevice === "Strava" && <StravaInfoButton onClick={() => setShowStravaInfo(true)} />}
            {linkedDevice === "Apple Health" && (
              <p className="w-full mb-0 text-sm">Manage this connection in the iOS app.</p>
            )}
            {linkedDevice === "Google Health Connect" && (
              <p className="w-full mb-0 text-sm">Manage this connection in the Android app.</p>
            )}
            {linkedDevice === "Fitbit" && (
              <div className="w-full rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-slate-900">
                {fitbitConnectionEnded && <p className="mb-2 font-semibold">Your Fitbit connection needs an update</p>}
                <p className="mb-2">
                  {fitbitConnectionEnded
                    ? "Google has ended support for this Fitbit connection, so it can no longer automatically sync your activity to My Challenge Canada. To continue syncing, update your connection through the My Challenge Canada app. It works on both iPhone and Android. Your previously saved activity is still in your account."
                    : "Google is ending support for this Fitbit connection. It will stop working on October 29, 2026 in Canada. Update your connection through the My Challenge Canada app, on iPhone or Android, to keep syncing your activity. Your saved activity will stay in your account."}
                </p>
                <p className="mb-1">Follow the steps for your phone:</p>
                <FitbitInstructionLinks />
              </div>
            )}
          </div>
          {linkedDevice === "Garmin" && <GarminToggle />}
        </div>
      )}
      {linkedDevice === "none" && (
        <>
          <ConnectProviderCards
            garminConnectEnabled={garminConnectEnabled}
            onGarminConnect={() => {
              setGarminConnectEnabled(false);
              requestGarminAuth();
            }}
            stravaOpen={showStravaConnect}
            onStravaToggle={() => setShowStravaConnect((prev) => !prev)}
            onStravaInfo={() => setShowStravaInfo(true)}
          />
          <div className="mb-4">
            {showStravaConnect && (
              <div className="anim-card w-full p-3 bg-slate-300 mb-3">
                <p>
                  You can automatically upload the following activities to your challenges by connecting your Strava account: Run (indoor or
                  outdoor), Trail Run, Walk, Hike, Virtual Run, Handcycle, Wheelchair.
                </p>
                <p>You can upload any other activities manually.</p>
                <p>
                  You can connect any sportwacth or fitness tracking to Strava. Only activities performed after the synchronization will be
                  uploaded.
                </p>
                <p>
                  Connecting your Strava account automatically enables activity uploads for your active challenges. New challenges have
                  automatic activity uploads enabled when you register.
                </p>
                <button className="w-full" onClick={(e) => requestStravaAuth()}>
                  <img src={process.env.PUBLIC_URL + "/img/btn_strava_connectwith_orange.png"} alt="Strava Button" />
                </button>
              </div>
            )}
          </div>
        </>
      )}
      <section className="my-6 rounded-md border border-slate-200 p-4">
        <h3 className="mb-1 font-semibold">Activity timezone</h3>
        <p className="mb-3 text-sm text-slate-600">
          Used when an activity provider does not include the activity’s original timezone.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            aria-label="Activity timezone"
            className="rounded border border-slate-300 px-3 py-2"
            list="activity-timezone-options"
            value={activityTimeZone}
            onChange={(event) => setActivityTimeZone(event.target.value)}
            placeholder="America/Toronto"
          />
          <datalist id="activity-timezone-options">
            {activityTimeZoneOptions.map((timeZone) => (
              <option key={timeZone} value={timeZone} />
            ))}
          </datalist>
          <button
            type="button"
            className="rounded border border-slate-300 px-3 py-2 disabled:opacity-50"
            onClick={saveActivityTimeZone}
            disabled={savingActivityTimeZone || !activityTimeZone.trim()}
          >
            {savingActivityTimeZone ? "Saving…" : "Save timezone"}
          </button>
          {activityTimeZoneMessage && <span role="status" className="text-sm">{activityTimeZoneMessage}</span>}
        </div>
      </section>
      <div className="grid grid-cols-[42px,1fr] items-center mb-3">
        <LocationIcon />
        <h5 className="font-semibold mb-0">Shipping Address</h5>
      </div>
      <div className="mb-5 w-full max-w-md rounded-md border border-slate-200 bg-slate-50 px-3 py-3 sm:ml-10 sm:px-4">
        <div className="grid gap-2 text-sm">
          <AddressDetail label="Address" value={userDetails?.address} />
          {userDetails?.addressLine2 && <AddressDetail label="Address Line 2" value={userDetails.addressLine2} />}
          <AddressDetail label="City" value={userDetails?.city} />
          <AddressDetail label="Province" value={userDetails?.state} />
          <AddressDetail label="Zip Code" value={userDetails?.postalCode} />
          <AddressDetail label="Country" value={userDetails?.country} />
          <AddressDetail label="Phone Number" value={userDetails?.phoneNumber} />
        </div>
        <p className="mb-0 mt-3 border-t border-slate-200 pt-3 text-sm text-slate-600">
          If you need to change your Shipping Address, please send us an email to{" "}
          <a className="font-semibold underline" href="mailto:hello@mychallengecanada.ca">
            hello@mychallengecanada.ca
          </a>
        </p>
      </div>
      <div className="grid grid-cols-[42px,1fr] items-center mb-3">
        <SprintIcon />
        <h5 className="font-semibold mb-0">Races Registered</h5>
      </div>
      <div className="grid gap-3 md:grid-cols-3">{racesMap ? racesMap : <Spinner />}</div>
      <div className="mt-8 border-t border-slate-200 pt-4">
        <Link
          className="text-sm font-medium text-slate-500 underline underline-offset-2 hover:text-slate-900"
          to="/account/delete"
        >
          Delete Account
        </Link>
      </div>
    </div>
  );
};

function StravaInfoButton({ onClick }) {
  return (
    <button
      type="button"
      aria-label="Learn about using Strava for free"
      className="flex h-5 w-5 items-center justify-center rounded-full border border-slate-400 bg-white text-xs font-bold leading-none text-slate-700 shadow-sm hover:bg-slate-50"
      onClick={onClick}
    >
      i
    </button>
  );
}

function StravaFreeInfoOverlay({ onClose }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="strava-free-info-title"
      onClick={onClose}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between gap-3">
          <h3 id="strava-free-info-title" className="mb-0 text-lg font-semibold text-slate-900">
            Strava Free Works
          </h3>
          <button
            type="button"
            aria-label="Close Strava information"
            className="rounded-md px-2 py-1 text-xl leading-none text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            onClick={onClose}
          >
            x
          </button>
        </div>
        <p className="mb-3 text-sm leading-6 text-slate-700">
          You can use the free version of Strava to connect your activities with your challenge.
        </p>
        <p className="mb-4 text-sm leading-6 text-slate-700">
          If Strava prompts you to try or upgrade to their premium membership, you can say no and keep using the free version.
        </p>
        <button
          type="button"
          className="w-full rounded-md bg-[#006bc1] px-4 py-2 text-sm font-semibold text-white hover:bg-[#005aa3]"
          onClick={onClose}
        >
          Got it
        </button>
      </div>
    </div>
  );
}

function AddressDetail({ label, value }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[120px,1fr] sm:gap-2">
      <p className="mb-0 font-semibold text-slate-600">{label}</p>
      <p className="mb-0 text-slate-900">{value || "-"}</p>
    </div>
  );
}

function calcFileSize(number) {
  if (number < 1e3) {
    return `${number} bytes`;
  } else if (number >= 1e3 && number < 1e6) {
    return `${(number / 1e3).toFixed(1)} KB`;
  }
  return `${(number / 1e6).toFixed(1)} MB`;
}

export default AccountPage;
